import React, { useCallback, useEffect, useState } from "react";
import { Send } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Modal from "../Modal/Modal";
import Input from "../Input/Input";
import Notification from "../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./InviteUserModal.module.scss";

const InviteUserModal = ({ isOpen, onClose, onInviteSuccess }) => {
  /* =========================
     STATE
  ========================= */
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [roles, setRoles] = useState([]);

  // Fixed casing for API: first letter uppercase
  const [inviteType] = useState("CLIENT");
  const roleTypeForFetch = inviteType === "CLIENT" ? "Client" : "Internal";

  const [emailError, setEmailError] = useState("");
  const [roleError, setRoleError] = useState("");

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  /* =========================
     API CALLS
  ========================= */
  const fetchRoles = useCallback(async () => {
    try {
      const query = new URLSearchParams({ type: roleTypeForFetch }).toString();
      const res = await apiGet("user", `/role?${query}`);
      setRoles(res?.data || []);
    } catch (error) {
      console.error("Failed to fetch roles", error);
    }
  }, [roleTypeForFetch]);

  /* =========================
     EFFECTS
  ========================= */
  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen, fetchRoles]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  /* =========================
     HANDLERS
  ========================= */
  const handleInvite = async () => {
    let isValid = true;

    if (!email.trim()) {
      setEmailError("This field is required");
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError("Enter a valid email address");
      isValid = false;
    }

    if (!roleId) {
      setRoleError("This field is required");
      isValid = false;
    }

    if (!isValid) return;

    setLoading(true);
    setEmailError("");
    setRoleError("");

    try {
      const res = await apiPost("user", "/invite-user", {
        email,
        inviteType, // "CLIENT" or "INTERNAL"
        roleId, // number from dropdown
      });

      setNotification({
        type: "success",
        message: res?.message || "User successfully invited!",
      });

      onInviteSuccess?.();

      setTimeout(() => {
        onClose();
        setEmail("");
        setRoleId("");
      }, 3000);
    } catch (error) {
      console.error("Invite failed", error);

      const errorMessage =
        error?.response?.data?.message || "User already active";

      setNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  if (!isOpen) return null;

  return (
    <Modal title="Invite User" onClose={onClose}>
      <div className={styles["admin-invite-user-modal__form"]}>
        {notification && (
          <div className={styles["admin-invite-user-modal__toast-container"]}>
            <Notification
              type={notification.type}
              message={notification.message}
            />
          </div>
        )}

        <Input
          label={
            <>
              <span>Email ID</span>
              <span style={{ color: "#d54747", marginLeft: "4px" }}>*</span>
            </>
          }
          type="email"
          placeholder="Enter email address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError("");
          }}
          error={emailError}
          required
        />

        <Input
          label={
            <>
              <span>Role</span>
              <span style={{ color: "#d54747", marginLeft: "4px" }}>*</span>
            </>
          }
          type="select"
          placeholder="Select Role"
          value={roleId}
          onChange={(e) => {
            setRoleId(Number(e.target.value));
            setRoleError("");
          }}
          options={roles.map((role) => ({
            label: role.name,
            value: role.id,
          }))}
          error={roleError}
          required
        />
      </div>

      <div className={styles["admin-invite-user-modal__actions"]}>
        <button
          className={styles["admin-invite-user-modal__btn"]}
          onClick={handleInvite}
          disabled={loading}
        >
          <Send size={16} strokeWidth={2.5} />
          {loading ? "Sending Invite..." : "Send Invite"}
        </button>
      </div>
    </Modal>
  );
};

export default InviteUserModal;
